import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { SplitLines } from "@/components/Reveal";
import { BRAND } from "@/lib/site";

const SLABS = [
  { src: "/images/tiles/calacatta.jpg", label: "Calacatta Oro · 1200 x 1800" },
  { src: "/images/tiles/nero.jpg", label: "Nero Marquina · 600 x 1200" },
  { src: "/images/tiles/statuario.jpg", label: "Statuario Bianco · 800 x 1600" },
];

export const Hero = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 60, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), { stiffness: 60, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yText = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const ySlab = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section ref={ref} onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }} data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden bg-[var(--bg)] grain">
      <div className="absolute inset-y-0 right-0 w-[55%] stripes opacity-70 [mask-image:linear-gradient(to_left,black,transparent)]" />
      <div className="absolute -left-40 top-1/3 h-[520px] w-[520px] rounded-full bg-[var(--teal-soft)] blur-3xl" />

      <div className="container-x relative grid min-h-[100svh] items-center gap-12 pt-28 pb-16 lg:grid-cols-12">
        <motion.div style={{ y: yText, opacity: fade }} className="lg:col-span-7 relative z-10">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.8 }} className="eyebrow mb-8">
            Manufacturer & Exporter · GVT / PGVT Tiles
          </motion.p>
          <SplitLines
            as="h1"
            delay={0.35}
            className="font-display text-[clamp(3rem,7vw,6.4rem)] font-light leading-[0.98] tracking-[-0.02em] text-[var(--navy)]"
            lines={["Surfaces that", <>travel <em className="italic font-normal text-[var(--mint)]">the world.</em></>, "Crafted in India."]}
          />
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.9 }} className="mt-8 max-w-xl text-base leading-relaxed text-[var(--muted)] sm:text-lg">
            {BRAND.name} manufactures large-format glazed vitrified tiles in Morbi, Gujarat and exports them to Europe, the Middle East, Australia, Russia, South East Asia and Latin America — directed from our head office in New Delhi.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 0.9 }} className="mt-10 flex flex-wrap gap-4">
            <Link to="/collection" data-testid="hero-explore-btn" className="btn-pill btn-navy">Explore the catalogue <ArrowUpRight size={16} className="arrow" /></Link>
            <Link to="/#contact" data-testid="hero-quote-btn" className="btn-pill btn-ghost">Request bulk quote</Link>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8, duration: 1 }} className="mt-14 flex flex-wrap gap-x-10 gap-y-4 font-mono text-[0.62rem] uppercase tracking-[0.22em] text-slate-500">
            <span>HQ — New Delhi</span><span>Factory — Morbi, Gujarat</span><span>7 Export Regions</span>
          </motion.div>
        </motion.div>

        <motion.div style={{ y: ySlab }} className="lg:col-span-5 relative h-[420px] sm:h-[520px] lg:h-[640px]" data-testid="hero-slabs">
          <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d", perspective: 1200 }} className="relative h-full w-full">
            {SLABS.map((s, i) => (
              <motion.figure
                key={s.src}
                initial={{ opacity: 0, y: 80, rotate: -6 + i * 3 }}
                animate={{ opacity: 1, y: 0, rotate: -6 + i * 4 }}
                transition={{ delay: 0.6 + i * 0.18, duration: 1.3, ease: [0.2, 0.8, 0.2, 1] }}
                style={{ translateZ: i * 60, left: `${8 + i * 14}%`, top: `${18 - i * 8}%` }}
                className="absolute w-[62%] aspect-[3/4] overflow-hidden rounded-[1.5rem] border border-white/70 shadow-[0_40px_80px_-30px_rgba(19,34,56,.5)]"
              >
                <img src={s.src} alt={s.label} className="h-full w-full object-cover" />
                <figcaption className="absolute bottom-3 left-3 rounded-full glass px-3 py-1 font-mono text-[0.58rem] uppercase tracking-widest text-[var(--navy)]">{s.label}</figcaption>
              </motion.figure>
            ))}
          </motion.div>
        </motion.div>
      </div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.2 }} className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-2 font-mono text-[0.6rem] uppercase tracking-[0.25em] text-slate-400">
        <ArrowDown size={12} className="animate-bounce" /> Scroll
      </motion.div>
    </section>
  );
};
